import {
  ACTION_GROUP_NODE_NAME,
  TRIGGER_GROUP_NODE_NAME,
} from "@repo/shared/migration-types";
import { DOCUMENT_GROUP_NODE_NAME } from "@repo/shared/migration-types";
import { RULE_GROUP_HOLDER_NODE_NAME } from "@repo/shared/migration-types";
import {
  type ActionNode,
  type DocumentNode,
  type EmailNode,
  type GroupNode,
  type NodeData,
  type ResultOutput,
  type RunStatus,
  aggregateModelDefinitions,
  isGroupNode,
} from "@repo/shared/workflow";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { produce } from "immer";
import { useMemo } from "react";
import { tsr } from ".";

export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      workflowVersionId,
      files,
    }: {
      taskId: string;
      workflowVersionId: string;
      files: Record<string, File>;
    }) => {
      // Try using a different way to send files -- maybe explicitly use formData
      const resp = await tsr.private.tasks.create.mutate({
        body: {
          name: taskId,
          workflowVersionId,
          ...files,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tasksOptions().queryKey,
      });
    },
  });
};

export const useRetryTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      await tsr.private.tasks.retry.mutate({
        params: { taskId },
        body: {},
      });
    },
    onSuccess: (_status, taskId) => {
      queryClient.invalidateQueries({ queryKey: taskOptions(taskId).queryKey });

      // Refetch runner state. Cleaner way to do this later.
      window.location.reload();
    },
  });
};

const tasksOptions = () => {
  return queryOptions({
    queryKey: ["tasks"],
    queryFn: async () => {
      const resp = await tsr.private.tasks.getAll.query({ query: {} });
      if (resp.status === 200) {
        return resp.body;
      }

      return null;
    },
  });
};

export const useTasks = () => {
  const query = useQuery(tasksOptions());

  return query;
};

const taskOptions = (taskId: string) => {
  return queryOptions({
    queryKey: ["tasks", taskId],
    queryFn: async () => {
      const resp = await tsr.private.tasks.getById.query({
        params: { taskId },
      });

      if (resp.status === 200) {
        return resp.body;
      }

      return null;
    },
  });
};

export const useTask = (taskId: string) => {
  return useQuery(taskOptions(taskId));
};

const workflowsOptions = () => {
  return queryOptions({
    queryKey: ["workflows"],
    queryFn: async () => {
      const resp = await tsr.private.workflows.getAll.query({ query: {} });
      if (resp.status === 200) {
        return resp.body;
      }

      return null;
    },
  });
};

export const useWorkflows = () => {
  const query = useQuery(workflowsOptions());

  return query;
};

const workflowOptions = (workflowId: string) => {
  return queryOptions({
    queryKey: ["workflows", workflowId],
    queryFn: async () => {
      const resp = await tsr.private.workflows.getById.query({
        params: { workflowId },
      });

      if (resp.status === 200) {
        return resp.body;
      }

      return null;
    },
  });
};

export const useWorkflow = (workflowId: string) => {
  const query = useQuery(workflowOptions(workflowId));

  return query;
};

const nodeOptions = <T = NodeData>(
  workflowId: string,
  nodeId?: string | null,
) => {
  return queryOptions({
    enabled: !!nodeId,
    queryKey: ["workflows", workflowId, "node", nodeId],
    queryFn: async () => {
      const resp = await tsr.private.workflows.getNode.query({
        // biome-ignore lint/style/noNonNullAssertion: guaranteed by `enabled`
        params: { workflowId, nodeId: nodeId! },
      });

      if (resp.status !== 200) {
        return undefined;
      }

      return resp.body.node as T;
    },
    // useWorkflow will set the initial data, so we set an infinite stale time
    // so that we don't waterfall every single node when the editor loads
    // biome-ignore lint/style/useNumberNamespace: Keeping it "Infinity" for react-query
    staleTime: Infinity,
  });
};

export const useNode = <T = NodeData>(
  workflowId: string,
  nodeId?: string | null,
) => {
  const qc = useQueryClient();
  const query = useQuery({
    ...nodeOptions<T>(workflowId, nodeId),
    initialData: () => {
      const resp = qc.getQueryData(workflowOptions(workflowId).queryKey);
      if (!resp) return undefined;

      const node = resp.workflow.nodes.find((node) => node.id === nodeId) as T;

      return node;
    },
  });

  return query;
};

export const useUpdateNode = (workflowId: string) => {
  const qc = useQueryClient();

  return useMutation({
    // For now just dump the entire node here
    mutationFn: ({ nodeId, update }: { nodeId: string; update: NodeData }) =>
      tsr.private.workflows.updateNode.mutate({
        body: { update },
        params: { workflowId, nodeId },
      }),
    onMutate: async ({ nodeId, update }) => {
      const old = qc.getQueryData(nodeOptions(workflowId, nodeId).queryKey);

      qc.setQueryData(nodeOptions(workflowId, nodeId).queryKey, update);

      qc.setQueryData(workflowOptions(workflowId).queryKey, (old) => {
        return produce(old, (draft) => {
          if (!draft) return;

          for (let i = 0; i < draft.workflow.nodes.length; i++) {
            if (draft.workflow.nodes[i].id === nodeId) {
              draft.workflow.nodes[i] = update;
              break;
            }
          }
        });
      });

      return { old };
    },
    onError: (error, { nodeId }, context) => {
      qc.setQueryData(nodeOptions(workflowId, nodeId).queryKey, () => {
        if (context) {
          return context.old;
        }
      });
    },
    onSettled: (data) => {
      if (data?.status === 200) {
        qc.invalidateQueries({
          queryKey: nodeOptions(workflowId, data?.body.updatedNode.id).queryKey,
        });
      }
    },
  });
};

export const useDeleteNode = (workflowId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId }: { nodeId: string; groupId?: string }) =>
      tsr.private.workflows.deleteNode.mutate({
        params: { workflowId, nodeId },
      }),
    onMutate: async ({ nodeId, groupId }) => {
      const oldNode = qc.getQueryData(nodeOptions(workflowId, nodeId).queryKey);
      qc.setQueryData(nodeOptions(workflowId, nodeId).queryKey, undefined);

      const oldGroup =
        groupId != null
          ? qc.getQueryData(nodeOptions(workflowId, groupId).queryKey)
          : undefined;

      if (groupId) {
        qc.setQueryData(nodeOptions(workflowId, groupId).queryKey, (old) => {
          if (!old) return old;

          return produce(old, (draft) => {
            draft.dependencyIds = draft.dependencyIds.filter(
              (id) => id !== nodeId,
            );
          });
        });
      }

      const oldWorkflow = qc.getQueryData(workflowOptions(workflowId).queryKey);
      if (oldWorkflow) {
        qc.setQueryData(workflowOptions(workflowId).queryKey, (old) => {
          return produce(old, (draft) => {
            if (!draft) return;

            draft.workflow.nodes = draft.workflow.nodes.filter(
              (n) => n.id !== nodeId,
            );
            for (const node of draft.workflow.nodes) {
              if (node.dependencyIds.includes(nodeId)) {
                node.dependencyIds = node.dependencyIds.filter(
                  (id) => id !== nodeId,
                );
              }
            }
          });
        });
      }

      return { oldNode, oldGroup, oldWorkflow };
    },
    onError: (error, { nodeId, groupId }, context) => {
      if (context?.oldNode) {
        qc.setQueryData(
          nodeOptions(workflowId, nodeId).queryKey,
          context.oldNode,
        );
      }

      if (context?.oldGroup) {
        qc.setQueryData(
          nodeOptions(workflowId, groupId).queryKey,
          context.oldGroup,
        );
      }

      if (context?.oldWorkflow) {
        qc.setQueryData(
          workflowOptions(workflowId).queryKey,
          context.oldWorkflow,
        );
      }
    },
    onSuccess: () => {
      // qc.invalidateQueries({
      //   queryKey: workflowOptions(workflowId).queryKey,
      // });
    },
  });
};

export const useCreateNode = (workflowId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ data, groupId }: { data: NodeData; groupId?: string }) =>
      tsr.private.workflows.createNode.mutate({
        body: { data, groupId },
        params: { workflowId },
      }),
    onSuccess: (data) => {
      if (data?.status === 200) {
        qc.setQueryData(
          nodeOptions(workflowId, data.body.node.id).queryKey,
          data.body.node,
        );

        qc.invalidateQueries({
          queryKey: workflowOptions(workflowId).queryKey,
        });
      }
    },
  });
};

export const useReparentNode = (workflowId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      nodeId,
      oldGroupId,
      newGroupId,
    }: {
      nodeId: string;
      oldGroupId: string;
      newGroupId: string;
    }) =>
      tsr.private.workflows.reparentNode.mutate({
        params: { workflowId, nodeId },
        body: { oldGroupId, newGroupId },
      }),
    onMutate: async ({ nodeId, oldGroupId, newGroupId }) => {
      // Cancel any outgoing refetches
      await qc.cancelQueries({
        queryKey: workflowOptions(workflowId).queryKey,
      });

      // Store old data
      const oldOldGroup = qc.getQueryData(
        nodeOptions(workflowId, oldGroupId).queryKey,
      );
      const oldNewGroup = qc.getQueryData(
        nodeOptions(workflowId, newGroupId).queryKey,
      );
      const oldWorkflow = qc.getQueryData(workflowOptions(workflowId).queryKey);

      // Optimistically update both groups
      qc.setQueryData(nodeOptions(workflowId, oldGroupId).queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          dependencyIds: old.dependencyIds.filter((id) => id !== nodeId),
        };
      });

      qc.setQueryData(nodeOptions(workflowId, newGroupId).queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          dependencyIds: [...old.dependencyIds, nodeId],
        };
      });

      qc.setQueryData(workflowOptions(workflowId).queryKey, (old) => {
        if (!old) return old;

        return produce(old, (draft) => {
          const oldNode = draft.workflow.nodes.find((n) => n.id === oldGroupId);
          const newNode = draft.workflow.nodes.find((n) => n.id === newGroupId);

          if (oldNode) {
            oldNode.dependencyIds = oldNode.dependencyIds.filter(
              (id) => id !== nodeId,
            );
          }

          if (newNode && !newNode.dependencyIds.includes(nodeId)) {
            newNode.dependencyIds = [...newNode.dependencyIds, nodeId];
          }
        });
      });

      return { oldOldGroup, oldNewGroup, oldWorkflow };
    },
    onError: (error, { oldGroupId, newGroupId }, context) => {
      // Restore old data on error
      if (context?.oldOldGroup) {
        qc.setQueryData(
          nodeOptions(workflowId, oldGroupId).queryKey,
          context.oldOldGroup,
        );
      }
      if (context?.oldNewGroup) {
        qc.setQueryData(
          nodeOptions(workflowId, newGroupId).queryKey,
          context.oldNewGroup,
        );
      }
      if (context?.oldWorkflow) {
        qc.setQueryData(
          workflowOptions(workflowId).queryKey,
          context.oldWorkflow,
        );
      }
    },
    onSettled: () => {
      // Invalidate relevant queries
      // qc.invalidateQueries({
      //   queryKey: workflowOptions(workflowId).queryKey,
      // });
    },
  });
};

export const useCreateExtractionModelFromFile = (workflowId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const resp =
        await tsr.private.workflows.createExtractionModelFromFile.mutate({
          params: { workflowId },
          body: { file },
        });

      if (resp.status !== 200) {
        throw new Error("Failed to create extraction model");
      }

      return resp.body.nodes;
    },
    onSuccess: (nodes) => {
      // Update the workflow cache to include the new nodes
      qc.setQueryData(workflowOptions(workflowId).queryKey, (old) => {
        return produce(old, (draft) => {
          if (!draft) return;

          // Replace nodes with the same ids
          const nodesToAdd = [];
          for (const node of nodes) {
            const index = draft.workflow.nodes.findIndex(
              (n) => n.id === node.id,
            );

            // If the node exists, replace it
            if (index !== -1) {
              draft.workflow.nodes[index] = node;
            } else {
              // Otherwise, add it to the list of nodes to add
              nodesToAdd.push(node);
            }
          }

          for (const node of nodesToAdd) {
            draft.workflow.nodes.push(node);
          }
        });
      });

      // Update individual node caches
      for (const node of nodes) {
        qc.setQueryData(nodeOptions(workflowId, node.id).queryKey, node);
      }
    },
  });
};

export const useDocument = (workflowId?: string, documentId?: string) => {
  return useQuery({
    enabled: !!documentId && !!workflowId,
    queryKey: ["workflows", workflowId, "document", documentId],
    queryFn: async () => {
      const resp = await tsr.private.workflows.getDocument.query({
        params: {
          // biome-ignore lint/style/noNonNullAssertion: guaranteed by enabled
          workflowId: workflowId!,
          // biome-ignore lint/style/noNonNullAssertion: guaranteed by enabled
          documentId: documentId!,
        },
      });

      if (resp.status === 200) {
        return resp.body;
      }

      return null;
    },
  });
};

export const resultOptions = <T = ResultOutput>(
  taskId: string,
  resultId?: string,
) => {
  return queryOptions({
    enabled: !!resultId,
    queryKey: ["tasks", taskId, "results", resultId],
    queryFn: async () => {
      const resp = await tsr.private.tasks.getResult.query({
        // biome-ignore lint/style/noNonNullAssertion: guaranteed by enabled
        params: { taskId, resultId: resultId! },
      });

      if (resp.status !== 200) {
        return undefined;
      }

      return resp.body.result as T;
    },
  });
};

export const useResult = <T = ResultOutput>(
  taskId: string,
  resultId?: string,
) => {
  const qc = useQueryClient();
  return useQuery({
    ...resultOptions<T>(taskId, resultId),
    initialData: () => {
      const taskData = qc.getQueryData(taskOptions(taskId).queryKey);
      if (!taskData) return undefined;

      // Only consider the first run for now
      const run = taskData.runs[0];
      const result = run.run.results.find((r) => r.id === resultId) as T;
      return result;
    },
  });
};

export const useUpdateResult = (taskId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      resultId,
      update,
    }: { resultId: string; update: ResultOutput }) =>
      tsr.private.tasks.updateResult.mutate({
        body: { update, resultId },
        params: { taskId },
      }),
    onMutate: async ({ resultId, update }) => {
      await qc.cancelQueries({
        queryKey: taskOptions(taskId).queryKey,
      });

      const previousTask = qc.getQueryData(taskOptions(taskId).queryKey);

      qc.setQueryData(taskOptions(taskId).queryKey, (old) => {
        return produce(old, (draft) => {
          if (!draft) return;

          // TODO(yogi): For now there's only one run per task
          const firstRun = draft.runs[0].run;
          firstRun.results = firstRun.results.map((result) =>
            result.id === resultId ? update : result,
          );
        });
      });

      return { previousTask };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTask) {
        qc.setQueryData(taskOptions(taskId).queryKey, context.previousTask);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({
        queryKey: taskOptions(taskId).queryKey,
      });
    },
  });
};

export const usePreviewAction = (taskId: string) => {
  return useMutation({
    mutationFn: async ({
      runId,
      nodeId,
    }: {
      runId: string;
      nodeId: string;
    }) => {
      const resp = await tsr.private.tasks.previewAction.mutate({
        params: { taskId },
        body: { runId, nodeId },
      });

      if (resp.status !== 200) {
        throw new Error("Failed to preview action");
      }

      return resp.body.result;
    },
  });
};

export const useRunAction = (taskId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      runId,
      nodeId,
      resultId,
    }: {
      runId: string;
      nodeId: string;
      resultId?: string;
    }) => {
      const resp = await tsr.private.tasks.runAction.mutate({
        params: { taskId },
        body: { runId, nodeId, resultId },
      });

      if (resp.status !== 200) {
        throw new Error("Failed to run action");
      }

      return resp.body.result;
    },
    onSuccess: (_resp, { resultId }) => {
      if (resultId) {
        qc.invalidateQueries({
          queryKey: resultOptions(taskId, resultId).queryKey,
        });
      }
      qc.invalidateQueries({
        queryKey: taskOptions(taskId).queryKey,
      });
    },
  });
};

export const useUploadExportExcelTemplate = (workflowId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeId, file }: { nodeId: string; file: File }) => {
      const resp = await tsr.private.workflows.uploadExportExcelTemplate.mutate(
        {
          params: { workflowId },
          body: { nodeId, file },
        },
      );

      if (resp.status !== 200) {
        throw new Error("Failed to upload Excel template");
      }

      return resp.body.updatedNode;
    },
    onSuccess: (node) => {
      qc.setQueryData(nodeOptions(workflowId, node.id).queryKey, node);

      qc.setQueryData(workflowOptions(workflowId).queryKey, (old) =>
        produce(old, (draft) => {
          if (!draft) return;

          const workflow = draft.workflow;
          const nodeIndex = workflow.nodes.findIndex((n) => n.id === node.id);
          if (nodeIndex !== -1) {
            workflow.nodes[nodeIndex] = node;
          }
        }),
      );
    },
  });
};

export const useStructuredWorkflow = (workflowId: string) => {
  const { data: workflowBody, isLoading } = useWorkflow(workflowId);
  const workflow = workflowBody?.workflow;

  const resp = useMemo(() => {
    const ruleGroupHolderNode = workflow?.nodes.find(
      (n) => isGroupNode(n) && n.name === RULE_GROUP_HOLDER_NODE_NAME,
    );
    const documentGroupNode = workflow?.nodes.find(
      (n) => isGroupNode(n) && n.name === DOCUMENT_GROUP_NODE_NAME,
    );
    const triggerGroupNode = workflow?.nodes.find(
      (n) => isGroupNode(n) && n.name === TRIGGER_GROUP_NODE_NAME,
    );

    const documentNodes = documentGroupNode?.dependencyIds.map((id) =>
      workflow?.nodes.find((n) => n.id === id),
    ) as DocumentNode[] | undefined;

    const ruleGroupNodes = workflow?.nodes.filter((n) =>
      ruleGroupHolderNode?.dependencyIds.includes(n.id),
    ) as GroupNode[] | undefined;

    const actionGroupNode = workflow?.nodes.find(
      (n) => isGroupNode(n) && n.name === ACTION_GROUP_NODE_NAME,
    );

    const actionNodes = actionGroupNode?.dependencyIds.map((id) =>
      workflow?.nodes.find((n) => n.id === id),
    ) as ActionNode[] | undefined;

    const triggerNodes = triggerGroupNode?.dependencyIds.map((id) =>
      workflow?.nodes.find((n) => n.id === id),
    ) as EmailNode[] | undefined; // only emails for now, soon api as well

    return {
      ruleGroupHolderNodeId: ruleGroupHolderNode?.id,
      documentGroupNodeId: documentGroupNode?.id,
      actionGroupNodeId: actionGroupNode?.id,
      triggerGroupNodeId: triggerGroupNode?.id,
      ruleGroupHolderNode,
      documentGroupNode,
      actionGroupNode,
      triggerGroupNode,
      documentNodes,
      actionNodes,
      ruleGroupNodes,
      triggerNodes,
    };
  }, [workflow?.nodes]);

  const { aggregateModel, variableStrings } = useMemo(() => {
    const aggregateModel = aggregateModelDefinitions(resp.documentNodes ?? []);

    const variableStrings = new Set<string>();
    if (aggregateModel.type !== "object") {
      return { aggregateModel, variableStrings };
    }

    for (const field of aggregateModel.fields ?? []) {
      if (field.type === "object") {
        for (const nestedField of field.fields ?? []) {
          variableStrings.add(`${field.name}.${nestedField.name}`);
        }
      }
    }

    return { aggregateModel, variableStrings };
  }, [resp.documentNodes]);

  return {
    ...resp,
    workflow,
    isLoading,
    aggregateModel,
    variableStrings,
  };
};

export const useCreateWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      const resp = await tsr.private.workflows.create.mutate({
        body: { name },
      });

      if (resp.status !== 200) {
        throw new Error("Failed to create workflow");
      }

      return resp.body.workflow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: workflowsOptions().queryKey,
      });
    },
  });
};

export const useDuplicateWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workflowId,
      name,
    }: { workflowId: string; name: string }) => {
      const resp = await tsr.private.workflows.duplicate.mutate({
        params: { workflowId },
        body: { name },
      });

      if (resp.status !== 200) {
        throw new Error("Failed to duplicate workflow");
      }

      return resp.body.workflow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: workflowsOptions().queryKey,
      });
    },
  });
};

export const useDeleteWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workflowId }: { workflowId: string }) => {
      const resp = await tsr.private.workflows.delete.mutate({
        params: { workflowId },
      });

      if (resp.status !== 200) {
        throw new Error("Failed to delete workflow");
      }

      return resp.body.success;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: workflowsOptions().queryKey,
      });
    },
  });
};

export const useUpdateExtractionModelFile = (workflowId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeId, file }: { nodeId: string; file: File }) => {
      const resp = await tsr.private.workflows.updateExtractionModelFile.mutate(
        {
          params: { workflowId },
          body: { nodeId, file },
        },
      );

      if (resp.status !== 200) {
        throw new Error("Failed to update extraction model file");
      }

      return resp.body.updatedNode;
    },
    onSuccess: (node) => {
      // Update the node cache
      qc.setQueryData(nodeOptions(workflowId, node.id).queryKey, node);

      // Update the workflow cache
      qc.setQueryData(workflowOptions(workflowId).queryKey, (old) =>
        produce(old, (draft) => {
          if (!draft) return;

          const workflow = draft.workflow;
          const nodeIndex = workflow.nodes.findIndex((n) => n.id === node.id);
          if (nodeIndex !== -1) {
            workflow.nodes[nodeIndex] = node;
          }
        }),
      );
    },
  });
};

export const useUpdateRunStatus = (taskId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (status: RunStatus) => {
      await tsr.private.tasks.updateRunStatus.mutate({
        params: { taskId },
        body: { status },
      });
    },
    onMutate: async (status) => {
      await qc.cancelQueries({ queryKey: taskOptions(taskId).queryKey });
      await qc.cancelQueries({ queryKey: tasksOptions().queryKey });

      const previousTask = qc.getQueryData(taskOptions(taskId).queryKey);
      qc.setQueryData(taskOptions(taskId).queryKey, (old) => {
        return produce(old, (draft) => {
          if (!draft) return;

          // Update the first run's status
          draft.runs[0].run.status = status;
        });
      });

      const previousTasks = qc.getQueryData(tasksOptions().queryKey);
      qc.setQueryData(tasksOptions().queryKey, (old) => {
        return produce(old, (draft) => {
          if (!draft) return;

          for (const t of draft.items) {
            if (t.id === taskId && t.latestRun) {
              t.latestRun.status = status;
            }
          }
        });
      });

      return { previousTask, previousTasks };
    },
    onError: (error, status, context) => {
      // Rollback to previous state on error
      if (context?.previousTask) {
        qc.setQueryData(taskOptions(taskId).queryKey, context.previousTask);
      }
      if (context?.previousTasks) {
        qc.setQueryData(tasksOptions().queryKey, context.previousTasks);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      qc.invalidateQueries({
        queryKey: taskOptions(taskId).queryKey,
      });
      qc.invalidateQueries({
        queryKey: tasksOptions().queryKey,
      });
    },
  });
};

export const useUpdateRPASessionStatus = (taskId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      resultId,
      action,
    }: {
      resultId: string;
      action: "pause" | "resume";
    }) => {
      const resp = await tsr.private.tasks.updateRPASessionStatus.mutate({
        params: { taskId },
        body: { resultId, action },
      });

      if (resp.status !== 200) {
        throw new Error("Failed to update RPA session status");
      }

      return resp.body.success;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: taskOptions(taskId).queryKey,
      });
    },
  });
};
